import { useState, useRef, useEffect } from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { CURRENCIES } from "@/lib/currency";
import { ChevronDown, Check, Globe } from "lucide-react";

export default function CurrencySwitcher({ variant = "header" }) {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const current = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  const isMobile = variant === "mobile";

  return (
    <div ref={ref} className={`relative ${isMobile ? "w-full" : ""}`} data-testid="currency-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-white/80 hover:text-[#D4AF37] transition-colors ${
          isMobile ? "w-full justify-between px-4 py-3" : "px-3 py-2"
        }`}
        data-testid="currency-switcher-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5" />
          <span className="text-[0.72rem] tracking-[0.16em] uppercase font-medium">
            {current.symbol} {current.code}
          </span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`${isMobile ? "static mt-2 w-full" : "absolute right-0 mt-2 w-44"} z-[70] bg-[#0F0F0F] border border-[#2A2A2A] shadow-xl`}
          role="listbox"
          data-testid="currency-switcher-list"
        >
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              type="button"
              role="option"
              aria-selected={c.code === currency}
              onClick={() => {
                setCurrency(c.code);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                c.code === currency
                  ? "text-[#D4AF37] bg-[#D4AF37]/8"
                  : "text-white/75 hover:text-[#D4AF37] hover:bg-[#1A1A1A]"
              }`}
              data-testid={`currency-option-${c.code}`}
            >
              <span className="flex items-center gap-3">
                <span className="font-serif w-5 text-base">{c.symbol}</span>
                <span className="tracking-[0.14em] text-xs uppercase font-medium">{c.label}</span>
              </span>
              {c.code === currency && <Check className="w-3.5 h-3.5 text-[#D4AF37]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
